/** Administración remota de NovaBerry POS.
 *
 *  Las dos apps comparten el mismo proyecto de Supabase. POS es la que
 *  atiende en cada local; esta es la central: desde el modo
 *  administrador se ven y se editan las sucursales, su stock, sus
 *  ventas, su caja, sus suscriptores y su configuración.
 *
 *  Por qué esto NO vive en el `Snapshot` del store:
 *
 *  El store carga todas sus tablas de una y las deja en memoria, porque
 *  el vendedor en terreno necesita la app entera funcionando con mala
 *  señal. Los datos de POS son de otra naturaleza —solo los mira el
 *  administrador, y las ventas de todos los locales pueden ser muchas
 *  filas—, así que se piden cuando se abren y no antes. Un vendedor no
 *  descarga nada de esto.
 *
 *  Reglas de convivencia con POS:
 *
 *  - El stock de una sucursal (`pos_stock`) NUNCA se escribe con un
 *    número absoluto desde acá: se llama a `pos_ajustar_stock_sucursal`,
 *    que hace la aritmética en la base y deja el movimiento anotado. Si
 *    el local vendió mientras esta pantalla estaba abierta, el ajuste se
 *    aplica igual sin borrar esa venta.
 *  - Mandar mercadería a un local es `pos_despachar_traspaso`, que
 *    descuenta de la bodega central y crea el envío en una sola
 *    transacción. El local confirma después lo que de verdad llegó.
 */

import { supabase, supabaseConfigurado } from './supabaseAdapter'

export { supabaseConfigurado }

/* --------------------------- Tipos --------------------------- */

/** "bodega": de acá sale el abastecimiento, no se atiende público ni
 *  hay caja. "local": punto de venta, con POS instalado. */
export type TipoSucursal = 'bodega' | 'local'

export const TIPOS_SUCURSAL: { valor: TipoSucursal; etiqueta: string }[] = [
  { valor: 'local', etiqueta: 'Local' },
  { valor: 'bodega', etiqueta: 'Bodega' },
]

export interface Sucursal {
  id: string
  nombre: string
  direccion: string | null
  comuna: string | null
  telefono: string | null
  activa: boolean
  tipo: TipoSucursal
  horario: string | null
  vendedorIds: string[] | null
  creada: string
  /** Cuánto se le paga al despachador por un envío a ESTA sucursal en
   *  particular. Sin definir (null/undefined), se usa la tarifa general
   *  de `ajustes.tarifaTraspasoSucursal` —la mayoría de las sucursales
   *  no necesita una propia, así que el default es "usar la general". */
  tarifaEnvio?: number | null
}

/** Producto del catálogo del local. Ojo que su `precio` es CON IVA —el
 *  cliente paga lo que dice la etiqueta—, a diferencia de los de la app
 *  de ventas, que son netos porque allá se factura a empresas. */
export interface ProductoPos {
  id: string
  codigo: string
  nombre: string
  unidad: string
  precioCosto: number
  precio: number
  activo: boolean
  /** Enlace opcional con el producto de la bodega central. Es lo que
   *  permite que un traspaso sepa de qué descontar allá. */
  productoOrigenId: string | null
}

export interface StockSucursal {
  id: string
  sucursalId: string
  productoId: string
  cantidad: number
  stockMinimo: number | null
  stockMaximo: number | null
  precio: number | null
  activo: boolean
  actualizado: string
}

export interface VentaPos {
  id: string
  folio: number
  sucursalId: string
  usuarioNombre: string
  clienteNombre: string | null
  total: number
  costoTotal: number
  utilidad: number
  estado: 'completada' | 'anulada'
  fecha: string
}

export interface TurnoPos {
  id: string
  sucursalId: string
  cajaId: string
  numero: number
  abiertoPorNombre: string | null
  apertura: string
  montoInicial: number
  cerradoPorNombre: string | null
  cierre: string | null
  efectivoEsperado: number | null
  efectivoContado: number | null
  diferencia: number | null
  estado: 'abierto' | 'cerrado'
}

/** La cuponera de un cliente: junta sellos y a los N canjea premio. El
 *  `codigo` es el que va en el QR —público a propósito, no expone el id
 *  del cliente. */
export interface Cuponera {
  id: string
  clienteId: string
  codigo: string
  ciclo: number
  sellos: number
  estado: 'activa' | 'premio_disponible'
  creada: string
  actualizada: string
}

export interface ConfigPos {
  id: string
  nombreNegocio: string
  porcentajeIva: number
  stockMinimoGlobal: number
  descuentoMaximoVendedor: number
  emitirDocumentos: boolean
  fidelidadActiva: boolean
  fidelidadMontoMinimo: number
  fidelidadSellosNecesarios: number
  fidelidadPremios: string[]
  sucursalPorDefecto: string | null
}

export interface Traspaso {
  id: string
  numero: number
  sucursalId: string
  estado: 'en_camino' | 'recibido' | 'anulado'
  despachadoPorNombre: string | null
  despacho: string
  recibidoPorNombre: string | null
  recepcion: string | null
  observaciones: string | null
  /** Quién se hizo cargo de llevarlo. Mientras esté en null, el envío
   *  salió de la bodega pero nadie lo tomó todavía: le aparece al
   *  despachador como trabajo por aceptar. */
  aceptadoPor?: string | null
  aceptadoPorNombre?: string | null
  aceptado?: string | null
  /** Lo que se le paga por este viaje, congelado al aceptarlo. Ver
   *  supabase/despachador.sql. */
  tarifa?: number | null
}

/* ------------------------- Utilidades ------------------------- */

function sb() {
  if (!supabase) throw new Error('Supabase no está configurado.')
  return supabase
}

/** "42P01" es "la tabla no existe": pasa si en este proyecto todavía no
 *  se corrieron las migraciones de POS. Se traduce a un mensaje que
 *  diga qué hacer, en vez del código crudo de Postgres. */
export function traducirError(e: { code?: string; message: string }): string {
  if (e.code === '42P01') {
    return 'NovaBerry POS todavía no está instalado en esta base de datos. Corre supabase/pos-schema.sql y pos-sucursales.sql.'
  }
  if (e.code === 'PGRST202') {
    return 'Falta actualizar la base de datos: no existe esa función de POS. Corre las migraciones de POS.'
  }
  return e.message
}

/** Levanta el resultado de una consulta, traduciendo el error si lo
 *  hubo. Todas las lecturas de acá pasan por esta puerta para que
 *  ninguna pantalla tenga que interpretar códigos de Postgres. */
async function leer<T>(
  consulta: PromiseLike<{ data: unknown; error: { code?: string; message: string } | null }>,
): Promise<T[]> {
  const { data, error } = await consulta
  if (error) throw new Error(traducirError(error))
  return (data as T[] | null) ?? []
}

/* --------------------------- Lecturas --------------------------- */

export async function traerSucursales(): Promise<Sucursal[]> {
  return leer<Sucursal>(sb().from('pos_sucursales').select('*').order('nombre'))
}

export async function traerProductosPos(): Promise<ProductoPos[]> {
  return leer<ProductoPos>(sb().from('pos_productos').select('*').order('nombre'))
}

export async function traerStock(sucursalId: string): Promise<StockSucursal[]> {
  return leer<StockSucursal>(
    sb().from('pos_stock').select('*').eq('sucursalId', sucursalId),
  )
}

/** Todo el stock de todos los locales, para el aviso de la pantalla de
 *  inicio: qué le está quedando poco a quién. */
export async function traerStockTodo(): Promise<StockSucursal[]> {
  return leer<StockSucursal>(sb().from('pos_stock').select('*'))
}

export async function traerVentas(sucursalId: string, limite = 50): Promise<VentaPos[]> {
  return leer<VentaPos>(
    sb()
      .from('pos_ventas')
      .select('*')
      .eq('sucursalId', sucursalId)
      .order('fecha', { ascending: false })
      .limit(limite),
  )
}

export async function traerTurnos(sucursalId: string, limite = 20): Promise<TurnoPos[]> {
  return leer<TurnoPos>(
    sb()
      .from('pos_turnos')
      .select('*')
      .eq('sucursalId', sucursalId)
      .order('apertura', { ascending: false })
      .limit(limite),
  )
}

export async function traerCuponeras(): Promise<Cuponera[]> {
  return leer<Cuponera>(
    sb().from('pos_cuponeras').select('*').order('actualizada', { ascending: false }),
  )
}

export async function traerTraspasos(sucursalId: string): Promise<Traspaso[]> {
  return leer<Traspaso>(
    sb()
      .from('pos_traspasos')
      .select('*')
      .eq('sucursalId', sucursalId)
      .order('despacho', { ascending: false }),
  )
}

/** Los envíos que salieron de la bodega y todavía no confirma nadie, de
 *  todos los locales. Es lo que pinta el punto verde "stock en camino"
 *  en la lista de sucursales: mirando solo `pos_stock` esa mercadería no
 *  existe en ninguna parte —ya salió de la bodega pero aún no entró al
 *  local—, y sin este aviso parece que se hubiera perdido. */
export async function traerTraspasosEnCamino(): Promise<Traspaso[]> {
  return leer<Traspaso>(
    sb()
      .from('pos_traspasos')
      .select('*')
      .eq('estado', 'en_camino')
      .order('despacho', { ascending: false }),
  )
}

/** Todos los envíos que ya aceptó alguien, para poder sumar lo que hay
 *  que pagarle por semana y por mes. */
export async function traerTraspasosAceptados(): Promise<Traspaso[]> {
  return leer<Traspaso>(
    sb()
      .from('pos_traspasos')
      .select('*')
      .not('aceptado', 'is', null)
      .order('aceptado', { ascending: false }),
  )
}

/** El despachador se hace cargo de un envío a sucursal.
 *
 *  La tarifa se congela acá con el valor que rige hoy: si mañana sube
 *  el pago por envío, este viaje se sigue pagando a lo que se acordó
 *  —igual que la comisión de una venta—.
 *
 *  Solo toma envíos que nadie haya aceptado: si dos personas aprietan
 *  al mismo tiempo, la segunda no pisa a la primera y no queda un viaje
 *  con dos dueños. */
export async function aceptarTraspaso(datos: {
  id: string
  usuarioId: string
  usuarioNombre: string
  tarifa: number
}): Promise<boolean> {
  const { data, error } = await sb()
    .from('pos_traspasos')
    .update({
      aceptadoPor: datos.usuarioId,
      aceptadoPorNombre: datos.usuarioNombre,
      aceptado: new Date().toISOString(),
      tarifa: datos.tarifa,
    })
    .eq('id', datos.id)
    .is('aceptado', null)
    .select('id')
  if (error) throw new Error(traducirError(error))
  return (data?.length ?? 0) > 0
}

export async function traerConfig(): Promise<ConfigPos | null> {
  const { data, error } = await sb().from('pos_config').select('*').eq('id', 'config').maybeSingle()
  if (error) throw new Error(traducirError(error))
  return (data as ConfigPos | null) ?? null
}

/* --------------------------- Escrituras --------------------------- */

export async function guardarSucursal(s: Partial<Sucursal> & { id: string }): Promise<void> {
  const { error } = await sb().from('pos_sucursales').upsert(s, { onConflict: 'id' })
  if (error) throw new Error(traducirError(error))
}

export async function guardarConfig(c: Partial<ConfigPos> & { id: string }): Promise<void> {
  const { error } = await sb().from('pos_config').upsert(c, { onConflict: 'id' })
  if (error) throw new Error(traducirError(error))
}

/** Deja el stock de un producto en un local EXACTAMENTE en `cantidad`.
 *
 *  El delta lo calcula la base contra lo que hay guardado en ese
 *  momento, no el navegador: si el local vendió mientras esta pantalla
 *  estaba abierta, esa venta no se borra. Queda anotado en
 *  `pos_movimientos` con su motivo, nunca en silencio. */
export async function ajustarStock(datos: {
  movimientoId: string
  sucursalId: string
  productoId: string
  cantidad: number
  motivo: string
  usuarioId: string
  usuarioNombre: string
}): Promise<number> {
  const { data, error } = await sb().rpc('pos_ajustar_stock_sucursal', {
    p_movimiento_id: datos.movimientoId,
    p_sucursal_id: datos.sucursalId,
    p_producto_id: datos.productoId,
    p_cantidad: datos.cantidad,
    p_tipo: 'ajuste',
    p_motivo: datos.motivo,
    p_usuario_id: datos.usuarioId,
    p_usuario_nombre: datos.usuarioNombre,
  })
  if (error) throw new Error(traducirError(error))
  return Number(data ?? 0)
}

/** Define el mínimo bajo el cual se avisa que a ese local le queda poco.
 *  Es el mismo número que mira POS, así que el aviso sale igual en las
 *  dos apps. */
export async function guardarMinimo(
  sucursalId: string,
  productoId: string,
  stockMinimo: number | null,
): Promise<void> {
  const { error } = await sb()
    .from('pos_stock')
    .update({ stockMinimo, actualizado: new Date().toISOString() })
    .eq('sucursalId', sucursalId)
    .eq('productoId', productoId)
  if (error) throw new Error(traducirError(error))
}

/** Manda mercadería de la bodega central a un local.
 *
 *  Descuenta de `productos` (la bodega) y crea el traspaso "en camino"
 *  en una sola transacción: nunca queda mercadería descontada sin su
 *  envío ni al revés. El local confirma después cuánto llegó de verdad
 *  —si algo se rompió en el camino, la diferencia queda registrada. */
export async function despacharTraspaso(datos: {
  id: string
  sucursalId: string
  despachadoPor: string
  despachadoPorNombre: string
  observaciones?: string
  items: {
    id: string
    productoId: string
    nombre: string
    cantidad: number
    costoUnitario: number
    productoOrigenId?: string
  }[]
  /** Servilletas, vasos, bombillas: van en el mismo envío que la fruta
   *  pero se descuentan de la tabla `insumos`, no de `productos`, y no
   *  entran al inventario del local —ver supabase/insumos.sql. Es
   *  opcional: un envío sin insumos se comporta como siempre. */
  insumos?: {
    id: string
    insumoId: string
    nombre: string
    cantidad: number
    costoUnitario: number
  }[]
}): Promise<{ duplicado: boolean; numero?: number }> {
  const { data, error } = await sb().rpc('pos_despachar_traspaso', { p_datos: datos })
  if (error) throw new Error(traducirError(error))
  return (data ?? { duplicado: false }) as { duplicado: boolean; numero?: number }
}

/* ------------------------- PIN de acceso ------------------------- */
//
// Mismo cálculo que `src/lib/cripto.ts` de POS: SHA-256 de sal + PIN,
// con una sal distinta por persona. Tiene que ser idéntico, porque el
// PIN que se define acá se teclea allá. Si uno de los dos cambia la
// fórmula, nadie puede entrar.

const nuevaSal = (): string => {
  const bytes = crypto.getRandomValues(new Uint8Array(16))
  return [...bytes].map((b) => b.toString(16).padStart(2, '0')).join('')
}

const hashearPin = async (pin: string, sal: string): Promise<string> => {
  const datos = new TextEncoder().encode(`novaberry:${sal}:${pin}`)
  const buffer = await crypto.subtle.digest('SHA-256', datos)
  return [...new Uint8Array(buffer)].map((b) => b.toString(16).padStart(2, '0')).join('')
}

export const LARGO_PIN = 4

/** Un PIN válido tiene 4 dígitos y no es una obviedad. Mismas reglas
 *  que POS, para no aceptar acá uno que allá se rechace. */
export function revisarPin(pin: string): string | null {
  if (!new RegExp(`^\\d{${LARGO_PIN}}$`).test(pin)) return `El PIN debe tener ${LARGO_PIN} dígitos.`
  if (/^(\d)\1+$/.test(pin)) return 'Elige un PIN menos evidente: no repitas el mismo dígito.'
  if ('0123456789'.includes(pin) || '9876543210'.includes(pin)) {
    return 'Elige un PIN menos evidente: no uses dígitos seguidos.'
  }
  return null
}

/** Le define el PIN a alguien y a qué sucursales puede entrar en POS.
 *  El PIN no se guarda nunca tal cual: se guarda su hash con sal. */
export async function definirAccesoPos(
  usuarioId: string,
  pin: string,
  sucursales: string[],
): Promise<void> {
  const pinSalt = nuevaSal()
  const pinHash = await hashearPin(pin, pinSalt)
  const { error } = await sb()
    .from('usuarios')
    .update({ pinHash, pinSalt, sucursales })
    .eq('id', usuarioId)
  if (error) throw new Error(traducirError(error))
}

/** Solo las sucursales, sin tocar el PIN. */
export async function definirSucursalesPos(
  usuarioId: string,
  sucursales: string[],
): Promise<void> {
  const { error } = await sb().from('usuarios').update({ sucursales }).eq('id', usuarioId)
  if (error) throw new Error(traducirError(error))
}
